// @ts-check

/**
 * Pixi-only entrypoint for the canvas branch.
 *
 * The legacy DOM renderer + runtime toggle system have been removed.
 * Pixi boot imports the engine (src/app.js) and wires it to the PresentationBridge.
 */

function installDebugLogOverlay() {
  if (window.__MATCH3_DEBUG_LOGS__) return;

  const overlay = document.getElementById('mobile-console');
  const output = document.getElementById('mobile-console-output');
  const closeBtn = document.getElementById('mobile-console-close');
  const entries = [];
  const maxEntries = 250;

  const formatArg = (value) => {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.stack || value.message || String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const render = () => {
    if (!output) return;
    output.textContent = entries.join('\n');
    output.scrollTop = output.scrollHeight;
  };

  const push = (level, args) => {
    const stamp = new Date().toLocaleTimeString([], { hour12: false });
    entries.push(`${stamp} [${level}] ${args.map(formatArg).join(' ')}`);
    if (entries.length > maxEntries) entries.splice(0, entries.length - maxEntries);
    render();
  };

  for (const level of ['log', 'info', 'warn', 'error']) {
    const original = console[level]?.bind(console);
    console[level] = (...args) => {
      push(level, args);
      original?.(...args);
    };
  }

  window.addEventListener('error', (event) => {
    push('error', [event.message || event.error || 'Unknown window error']);
  });
  window.addEventListener('unhandledrejection', (event) => {
    push('error', [event.reason || 'Unhandled promise rejection']);
  });

  closeBtn?.addEventListener('click', () => {
    if (overlay) overlay.hidden = true;
  });

  window.__MATCH3_DEBUG_LOGS__ = {
    show() {
      if (!overlay) return;
      overlay.hidden = false;
      render();
    },
    hide() {
      if (overlay) overlay.hidden = true;
    },
    entries,
  };
}

function markAppReady() {
  document.body.classList.remove('app-booting');
  document.body.classList.add('app-ready');
}

// Apply designer-preview body class immediately so #pixi-shell is visible
// and the layout is correct before Pixi initializes.
if (new URLSearchParams(window.location.search).get('preview') === '1') {
  document.body.classList.add('designer-preview');
}

async function main() {
  installDebugLogOverlay();
  // Mark managed so Pixi bootstrap doesn't auto-boot twice.
  window.__MATCH3_RENDERER_MANAGED__ = true;

  const pixiModule = await import('./presentation/pixi/bootstrapPixi.ts');
  if (typeof pixiModule.bootPixiRenderer === 'function') {
    const root = await pixiModule.bootPixiRenderer();
    if (!root) {
      throw new Error('Pixi boot returned null (missing #pixi-root mount?)');
    }
    markAppReady();
    return;
  }

  throw new Error('Pixi bootstrap module missing bootPixiRenderer export');
}

main().catch(err => {
  document.body.classList.remove('app-booting');
  console.error('[main] Fatal initialization error:', err);
  document.body.innerHTML = `
    <div style="padding: 40px; text-align: center; font-family: sans-serif;">
      <h1>Match-3 Failed to Load</h1>
      <p>Pixi renderer could not be initialized.</p>
      <pre style="text-align: left; max-width: 900px; margin: 20px auto; white-space: pre-wrap; color: #b00020;">${String(err?.stack || err)}</pre>
    </div>
  `;
});
